import { useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { TabDragScope } from './TabDragScope';
import { TabKeyboardSensor } from './TabKeyboardSensor';
import { TabPointerSensor } from './TabPointerSensor';

export function useTabDragSensors(isMobile: boolean, desktopMinWidth: number) {
  const media = useMemo(
    () =>
      typeof window === 'undefined'
        ? null
        : window.matchMedia(`(min-width: ${desktopMinWidth}px) and (pointer: fine)`),
    [desktopMinWidth],
  );
  const subscribe = useCallback(
    (onChange: () => void) => {
      media?.addEventListener('change', onChange);
      return () => media?.removeEventListener('change', onChange);
    },
    [media],
  );
  const desktopPointer = useSyncExternalStore(
    subscribe,
    () => media?.matches ?? false,
    () => false,
  );
  const canReorder = !isMobile && desktopPointer;
  const [scope] = useState(() => new TabDragScope());
  useLayoutEffect(() => {
    if (canReorder) scope.activate();
    // 卸载是静默销毁，不调用onEnd/onCancel更新已卸载的DndContext。
    return () => scope.dispose();
  }, [scope, canReorder]);
  const pointer = useSensor(TabPointerSensor, { scope });
  const keyboard = useSensor(TabKeyboardSensor, {
    scope,
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(
    canReorder ? pointer : undefined,
    canReorder ? keyboard : undefined,
  );
  return { sensors, canReorder };
}
